// src/pages/buyLicense/BuyLicense.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Divider,
  Space,
  Spin,
  Typography,
  Modal,
} from "antd";
import api from "../../services/api";
import BuyLicensePlanSelect from "./BuyLicenseSelectPlan";
import BuyLicenseNecessaryList from "./BuyLicenseNecessaryList";
import BuyLicenseInvoiceProfileSelect from "./BuyLicenseInvoiceProfileSelect";
import BuyLicenseInvoiceMiniCard from "./BuyLicenseInvoiceMiniCard";
import BuyLicenseCheckoutSummary from "./BuyLicenseCheckoutSummary";
import useLicenseProducts from "src/hooks/useLicenseProducts";
import useInvoiceProfiles from "src/hooks/useInvoiceProfiles";
import { LicenseRow } from "src/reducers/subscription";
import { useLanguage } from "src/providers/LanguageProvider";
import { addResolverProductId, setHasW } from "src/store/generalSlice";
import { RESOLVER_LICENSE_PRODUCT_IDS } from "src/pages/resolver/resolverProducts";
import { showResolverApiKey } from "src/pages/resolver/showResolverApiKey";
import { useDispatch } from "react-redux";

const { Text } = Typography;

/* ---- Helpers ---- */
const getPrice = (n: any) => Number(n?.Price ?? n?.price ?? 0);
const sumTree = (nodes: any[], visited = new Set<number>()) => {
  let monthly = 0;
  let oneTime = 0;
  for (const node of nodes || []) {
    if (!node || visited.has(node.Id)) continue;
    visited.add(node.Id);

    const price = getPrice(node);
    if (node.IsMonthly) monthly += price;
    else oneTime += price;

    if (Array.isArray(node.children) && node.children.length > 0) {
      const childSum = sumTree(node.children, visited);
      monthly += childSum.monthly;
      oneTime += childSum.oneTime;
    }
  }
  return { monthly, oneTime };
};

const Section: React.FC<{ title: string; divider?: boolean; children?: React.ReactNode }> = ({
  title,
  divider = true,
  children,
}) => (
  <>
    {divider ? (
      <Divider orientation="left" style={{ margin: "8px 0" }}>
        {title}
      </Divider>
    ) : (
      <Text strong>{title}</Text>
    )}
    {children}
  </>
);

export default function BuyLicense({
  mainLicense,
  open,
  onCancel,
  onSubmit,
}: {
  mainLicense?: LicenseRow | null;
  open: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}) {
  const { t } = useLanguage();
  const { message: messageApi, modal } = App.useApp();

  // Controlled state
  const [plan, setPlan] = useState<number | string | null>(null);
  const [interval, setInterval] = useState<string>(""); // şu an opsiyonel
  const [amount, setAmount] = useState<number | undefined>(undefined); // şu an opsiyonel
  const [invoiceProfileId, setInvoiceProfileId] = useState<number | string | null>(null);

  const invoiceProfiles = useInvoiceProfiles();
  const licenseProducts = useLicenseProducts();
  const subLicenseProducts = useLicenseProducts();
  const dispatch = useDispatch();

  useEffect(() => {
    // reset state
    licenseProducts.reset();
    subLicenseProducts.reset();
    setPlan(null);
    setInterval("");
    setAmount(undefined);
    setInvoiceProfileId(null);

    if (!open) return;

    if (!mainLicense) {
      licenseProducts.fetchProducts({ parentId: 0, isPurchasable: 1 });
    } else {
      licenseProducts.fetchPurchasableProducts(mainLicense.Id);
      setInvoiceProfileId(mainLicense.invoiceInfoId);
    }
    invoiceProfiles.fetchInvoiceProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!plan) return;
    subLicenseProducts.fetchProducts({
      parentId: plan,
      isRequired: 1,
      recursiveRequired: 1,
    });
  }, [plan]);

  const selectedInvoice = useMemo(
    () =>
      (invoiceProfiles.invoiceProfiles || []).find(
        (p) => String(p.Id) === String(invoiceProfileId)
      ),
    [invoiceProfiles.invoiceProfiles, invoiceProfileId]
  );

  const selectedPlan = useMemo(
    () =>
      (licenseProducts.products || []).find(
        (p: any) => String(p?.Id) === String(plan)
      ),
    [licenseProducts.products, plan]
  );

  const necessaryItems = useMemo(
    () => [selectedPlan, ...(subLicenseProducts.products || [])].filter(Boolean),
    [selectedPlan, subLicenseProducts.products]
  );

  // Totals
  const { monthlyTotal, oneTimeTotal } = useMemo(() => {
    const visited = new Set<number>();
    let monthly = 0,
      oneTime = 0;

    if (plan) {
      const selected = (licenseProducts.products || []).find(
        (p: any) => String(p?.Id) === String(plan)
      );
      if (selected) {
        const s = sumTree([selected], visited);
        monthly += s.monthly;
        oneTime += s.oneTime;
      }
    }

    const subSum = sumTree(subLicenseProducts.products || [], visited);
    monthly += subSum.monthly;
    oneTime += subSum.oneTime;

    return { monthlyTotal: monthly, oneTimeTotal: oneTime };
  }, [plan, licenseProducts.products, subLicenseProducts.products]);

  // Button state
  const isOkDisabled =
    licenseProducts.loading ||
    !plan ||
    (!mainLicense && !invoiceProfileId);

  const [loadingBuy, setLoadingBuy] = useState(false);

  const handlePlanChange = (value: number | string) => {
    setPlan(value);
    // plan değişince opsiyonel alanları sıfırla
    setInterval("");
    setAmount(undefined);
  };

  const handleOk = async () => {
    if (!plan) {
      messageApi.error(t("ERROR_SELECT_PLAN"));
      return;
    }
    if (!mainLicense && !invoiceProfileId) {
      messageApi.error(t("ERROR_SELECT_INVOICE_PROFILE"));
      return;
    }

    setLoadingBuy(true);
    try {
      const res: any = await api.post("/licenses", {
        plan,
        invoiceProfileId,
        parentId: mainLicense?.Id || 0,
      });

      if(plan == 13){
        dispatch(setHasW(true))
      }

      if (RESOLVER_LICENSE_PRODUCT_IDS.includes(Number(plan))) {
        dispatch(addResolverProductId(Number(plan)));
        if (res?.data?.resolverApiKey) showResolverApiKey(modal, res.data.resolverApiKey, t);
      }

      onSubmit?.({
        plan,
        interval,
        amount,
        monthlyTotal,
        oneTimeTotal,
        invoiceProfileId,
      });


      messageApi.success(t("PURCHASE_SUCCESS"));
    } catch (e) {
      messageApi.error(t("PURCHASE_FAILED"));
    } finally {
      setLoadingBuy(false);
    }
  };

  return (
    <Modal
      title={
        mainLicense
          ? t("MODAL_TITLE_NEW_CHILD")
          : t("MODAL_TITLE_NEW_SUBSCRIPTION")
      }
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText={t("BTN_BUY")}
      cancelText={t("BTN_CANCEL")}
      destroyOnClose
      okButtonProps={{ disabled: isOkDisabled, loading: loadingBuy }}
    >
      <Spin spinning={loadingBuy}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <BuyLicensePlanSelect
            value={plan as any}
            options={
              (licenseProducts.products || []).map((p: any) => ({
                label: p.Name,
                value: p.Id,
              })) || []
            }
            loading={licenseProducts.loading}
            onChange={handlePlanChange}
            error={licenseProducts.error}
          />

          <BuyLicenseNecessaryList
            loading={subLicenseProducts.loading}
            error={subLicenseProducts.error}
            items={necessaryItems as any}
          />

          <Section title={t("SECTION_INVOICE_INFO")} />

          {!mainLicense && (
            <BuyLicenseInvoiceProfileSelect
              value={invoiceProfileId as any}
              options={
                (invoiceProfiles.invoiceProfiles || []).map((p: any) => ({
                  label: p.CompanyName,
                  value: p.Id,
                })) || []
              }
              loading={invoiceProfiles.loading}
              error={invoiceProfiles.error}
              onChange={setInvoiceProfileId as any}
            />
          )}

          <BuyLicenseInvoiceMiniCard profile={selectedInvoice as any} />

          <BuyLicenseCheckoutSummary
            monthlyTotal={monthlyTotal}
            oneTimeTotal={oneTimeTotal}
            invoiceProfile={selectedInvoice as any}
          />
        </Space>
      </Spin>
    </Modal>
  );
}
